import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Bar, Icon, Ring, ReqRow, SectionLabel, Segmented, TypeBadge, MobileHeader, EmptyState, type IconName } from '@/components';
import { useCourse, useEnrollments } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';
import { requirementState, courseGating, issuanceMode } from '@/lib/requirements';
import { REQ } from '@/data/dictionaries';
import { formatDuration } from '@/lib/format';

const STRIPE = 'repeating-linear-gradient(135deg, rgba(255,255,255,.16), rgba(255,255,255,.16) 1px, transparent 1px, transparent 10px)';

type Tab = 'lessons' | 'reqs' | 'assess';

export function MobileCourseDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { L } = useLocalized();
  const { data: course } = useCourse(id);
  const { data: enrollments = [] } = useEnrollments();
  const [tab, setTab] = useState<Tab>('lessons');

  if (!course) return <EmptyState icon="book" title="Course not found" />;
  const e = enrollments.find((x) => x.courseId === id);
  const enrolled = !!e;
  const rs = requirementState(course, e);
  const met = course.requirements.filter((k) => rs[k]?.met).length;
  const remaining = course.requirements.length - met;
  const g = courseGating(course, e);

  // The single next action — resume the course rather than re-read the catalog.
  const nextLessonIdx = g.lessonsLocked ? -1 : course.lessons.findIndex((l) => !l.done);
  const cont: { label: string; to: string; ic: IconName } | null = !enrolled
    ? null
    : g.lessonsLocked
      ? { label: 'Start pre-test', to: `/learn/course/${id}/test/pre`, ic: 'doc' }
      : nextLessonIdx >= 0
        ? { label: `Continue · ${course.lessons[nextLessonIdx].title.en}`, to: `/learn/learning/${id}/lesson/${nextLessonIdx}`, ic: 'play' }
        : g.postTestLocked
          ? { label: 'Check in at the seminar', to: `/learn/course/${id}/attendance`, ic: 'qr' }
          : course.requirements.includes('posttest') && !rs['posttest']?.met
          ? { label: 'Take the post-test', to: `/learn/course/${id}/test/post`, ic: 'doc' }
          : g.evalRequired && !g.evalDone
            ? { label: 'Course evaluation', to: `/learn/course/${id}/eval`, ic: 'star' }
            : null;

  const facts: { ic: IconName; l: string; v: string }[] = [
    { ic: 'clock', l: 'Online lessons', v: formatDuration(course.lessons.reduce((s, l) => s + (l.minutes ?? 0), 0)) },
    { ic: 'award', l: 'Certificate valid', v: course.certYears ? `${course.certYears} year${course.certYears > 1 ? 's' : ''}` : '—' },
    { ic: 'cal', l: 'Enrollment', v: `${course.enrollStart}–${course.enrollEnd}` },
    { ic: 'users', l: 'Seats', v: `${course.capacity - course.enrolled} of ${course.capacity} left` },
  ];
  // Enrolled learners don't need the marketing facts (seats / enrollment window).
  const shownFacts = enrolled ? facts.slice(0, 2) : facts;

  const Requirements = (
    <div>
      <SectionLabel action={<span className="u-mono" style={{ fontSize: 12, color: enrolled ? 'var(--sage-deep)' : 'var(--ink-3)' }}>{met}/{course.requirements.length}</span>}>
        Certificate requirements · เงื่อนไขใบรับรอง
      </SectionLabel>
      <div className="card" style={{ padding: '4px 16px' }}>
        {course.requirements.map((k, i) => (
          <div key={k} style={{ borderTop: i ? '1px solid var(--line)' : 'none' }}>
            <ReqRow met={rs[k].met} pending={rs[k].pending} label={REQ[k].en} th={REQ[k].th} detail={rs[k].detail} />
          </div>
        ))}
      </div>
      {enrolled && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, padding: '11px 14px', background: 'var(--sage-tint)', borderRadius: 10, fontSize: 13, color: 'var(--sage-deep)' }}>
          <Icon name="shield" size={16} style={{ flex: 'none', marginTop: 1 }} />
          <span style={{ textWrap: 'pretty' }}><strong>{remaining} requirement{remaining !== 1 ? 's' : ''} left.</strong> {issuanceMode(course) === 'manual' ? 'Your certificate is issued after instructor approval once all are met.' : 'Your certificate issues automatically when all are met.'}</span>
        </div>
      )}
    </div>
  );

  const Lessons = (
    <>
      {g.lessonsLocked && (
        <div className="card" style={{ padding: 16, background: 'var(--ochre-soft)', borderColor: 'transparent' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ color: 'var(--ochre)' }}><Icon name="lock" size={20} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600 }}>Take the pre-test first</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2, textWrap: 'pretty' }}>A short pre-test unlocks the lessons and sets your baseline.</div>
              <button className="btn btn-sm" style={{ marginTop: 12, background: 'var(--ochre)', color: 'var(--on-accent)' }} onClick={() => navigate(`/learn/course/${id}/test/pre`)}>
                <Icon name="doc" size={15} /> Start pre-test
              </button>
            </div>
          </div>
        </div>
      )}
      {course.lessons.length > 0 ? (
        <div className="card divide">
          {course.lessons.map((l, i) => {
            const locked = !enrolled || g.lessonsLocked || (i > 0 && !course.lessons[i - 1].done);
            return (
              <button key={l.id} onClick={() => !locked && navigate(`/learn/learning/${id}/lesson/${i}`)} style={{ all: 'unset', display: 'block', width: '100%', cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.55 : 1 }}>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, flex: 'none', display: 'grid', placeItems: 'center', background: l.done ? 'var(--sage)' : 'var(--paper-2)', color: l.done ? '#fbfaf7' : 'var(--ink-3)' }}>
                      {l.done ? <Icon name="check" size={15} stroke={2.4} /> : <Icon name={l.kind === 'video' ? 'play' : 'file'} size={14} fill={l.kind === 'video'} />}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 500, lineHeight: 1.3 }}>{l.title.en}</div>
                      <div className="th" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{l.title.th} · {l.minutes} min</div>
                    </div>
                  </div>
                  {locked ? <Icon name="lock" size={15} style={{ color: 'var(--ink-4)' }} /> : <Icon name="chevR" size={16} style={{ color: 'var(--ink-4)' }} />}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13.5 }}>No online lessons in this course.</div>
      )}
    </>
  );

  const Assessment = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {course.seminar && (
        <div className="card" style={{ padding: 16, background: 'var(--clay-soft)', borderColor: 'transparent' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ color: 'var(--clay)' }}><Icon name="pin" size={20} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600 }}>Onsite session · {course.seminar}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>{course.venue} · 09:00–16:00</div>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" style={{ flex: '1 0 45%', color: rs['posttest']?.met ? 'var(--sage-deep)' : undefined }} disabled={g.postTestLocked || rs['posttest']?.met} onClick={() => navigate(`/learn/course/${id}/test/post`)}><Icon name={rs['posttest']?.met ? 'checkCircle' : g.postTestLocked ? 'lock' : 'doc'} size={16} /> {rs['posttest']?.met ? 'Post-test done' : 'Post-test'}</button>
        {rs['posttest']?.met && (
          <button className="btn btn-ghost" style={{ flex: '1 0 45%' }} onClick={() => navigate(`/learn/course/${id}/review`)}><Icon name="search" size={16} /> Review answers</button>
        )}
        {course.requirements.includes('attend') && (
          <button className="btn btn-ghost" style={{ flex: '1 0 45%' }} disabled={g.checkinLocked} onClick={() => navigate(`/learn/course/${id}/attendance`)}><Icon name={g.checkinLocked ? 'lock' : 'qr'} size={16} /> Check-in</button>
        )}
        {g.evalRequired && (
          <button className="btn btn-ghost" style={{ flex: '1 0 45%', color: g.evalDone ? 'var(--sage-deep)' : undefined }} onClick={() => navigate(`/learn/course/${id}/eval`)}>
            <Icon name={g.evalDone ? 'checkCircle' : 'star'} size={16} /> {g.evalDone ? 'Evaluation done' : 'Evaluation'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader title={course.title.en} th={L(course.title)} onBack={() => navigate(-1)} large={false} />
      <div style={{ paddingBottom: 28 }}>
        {course.coverUrl ? (
          // Show the uploaded cover in full (natural ratio) instead of cropping
          // it to a fixed box.
          <div style={{ position: 'relative' }}>
            <img src={course.coverUrl} alt={course.title.en} style={{ display: 'block', width: '100%', height: 'auto' }} />
            <div style={{ position: 'absolute', left: 18, bottom: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <TypeBadge type={course.type} />
              <Badge kind="line" style={{ background: 'rgba(251,250,247,.85)' }}>{course.code}</Badge>
            </div>
          </div>
        ) : (
          <div style={{ aspectRatio: enrolled ? '4 / 1' : '3 / 1', background: course.cover, position: 'relative', display: 'flex', alignItems: 'flex-end', padding: 18 }}>
            <div style={{ position: 'absolute', inset: 0, background: STRIPE }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', position: 'relative' }}>
              <TypeBadge type={course.type} />
              <Badge kind="line" style={{ background: 'rgba(251,250,247,.85)' }}>{course.code}</Badge>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '20px 20px 0' }}>
          {enrolled ? (
            <>
              {/* Continue-learning header replaces the catalog framing once enrolled. */}
              <div className="card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <Ring value={e!.progress} size={48} stroke={5} label={false} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{e!.progress}% complete</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 1 }}>{met}/{course.requirements.length} requirements met</div>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}><Bar value={e!.progress} /></div>
                {cont && (
                  <button className="btn btn-primary btn-block" style={{ marginTop: 14 }} onClick={() => navigate(cont.to)}>
                    <Icon name={cont.ic} size={16} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cont.label}</span>
                  </button>
                )}
              </div>

              <Segmented
                value={tab}
                onChange={setTab}
                size="sm"
                block
                options={[
                  { value: 'lessons', label: 'Lessons' },
                  { value: 'reqs', label: 'Requirements' },
                  { value: 'assess', label: 'Assessment' },
                ]}
              />

              {tab === 'lessons' && Lessons}
              {tab === 'reqs' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {Requirements}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: 'var(--line)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)' }}>
                    {shownFacts.map((f, i) => (
                      <div key={i} style={{ background: 'var(--card)', padding: '13px 14px' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ink-3)', fontSize: 12, marginBottom: 6 }}><Icon name={f.ic} size={14} /> {f.l}</div>
                        <div style={{ fontSize: 14.5, fontWeight: 500 }}>{f.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {tab === 'assess' && Assessment}
            </>
          ) : (
            <>
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ink-3)', fontSize: 13, marginBottom: 8 }}>
                  <Icon name="bldg" size={15} /> Hosted by {course.host}
                </div>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'var(--ink-2)', textWrap: 'pretty' }}>{course.summary}</p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: 'var(--line)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)' }}>
                {facts.map((f, i) => (
                  <div key={i} style={{ background: 'var(--card)', padding: '13px 14px' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ink-3)', fontSize: 12, marginBottom: 6 }}><Icon name={f.ic} size={14} /> {f.l}</div>
                    <div style={{ fontSize: 14.5, fontWeight: 500 }}>{f.v}</div>
                  </div>
                ))}
              </div>

              {course.seminar && (
                <div className="card" style={{ padding: 16, background: 'var(--clay-soft)', borderColor: 'transparent' }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ color: 'var(--clay)' }}><Icon name="pin" size={20} /></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 600 }}>Onsite session · {course.seminar}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>{course.venue} · 09:00–16:00</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Enroll CTA sits ABOVE the requirements so it's seen without
                  scrolling past the (long) requirement list. */}
              {g.canEnroll ? (
                <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate(`/learn/catalog/${id}/enroll`)}>
                  ลงทะเบียนเรียน <Icon name="arrowR" size={17} />
                </button>
              ) : (
                <div>
                  <button className="btn btn-lg btn-block" disabled style={{ background: 'var(--paper-2)', color: 'var(--ink-3)' }}>
                    <Icon name="lock" size={16} /> {g.capacityFull ? 'Class full' : 'Enrollment closed'}
                  </button>
                  <p style={{ margin: '8px 0 0', textAlign: 'center', fontSize: 12, color: 'var(--ink-3)', textWrap: 'pretty' }}>
                    {g.capacityFull ? 'All seats are taken for this course.' : 'This course is not open for enrollment right now.'}
                  </p>
                </div>
              )}
              {Requirements}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
